import { useEffect, useMemo, useRef, useState } from 'react';

const PETAL_PATH =
  'M20 2 C 8 14, 4 32, 12 50 C 16 56, 24 56, 28 50 C 36 32, 32 14, 20 2 Z';

interface PetalSpec {
  left: string;
  size: number;
  delay: string;
  duration: string;
  rotate: string;
}

function buildPetals(count: number): PetalSpec[] {
  const out: PetalSpec[] = [];
  for (let i = 0; i < count; i++) {
    const seed = i * 9301 + 49297;
    const r = (n: number) => ((Math.sin(seed * (n + 1)) + 1) / 2);
    out.push({
      left: `${(r(1) * 100).toFixed(2)}%`,
      size: 22 + Math.floor(r(2) * 22),
      delay: `-${(r(3) * 28).toFixed(2)}s`,
      duration: `${(28 + r(4) * 14).toFixed(2)}s`,
      rotate: `${(r(5) * 360).toFixed(0)}deg`,
    });
  }
  return out;
}

interface Props {
  /** quantidade de pétalas (default 5 — mantém leve) */
  petals?: number;
  /** intensidade da aurora (default 1) */
  intensity?: number;
}

/**
 * Background vivo, leve. Aurora estática (radial gradients sem blur pesado nem
 * mix-blend-mode), pétalas em CSS keyframes. Zero compositing layers extras
 * para não interferir com scroll. Posicionado fixed atrás de tudo.
 */
export function AmbientBackground({ petals = 5, intensity = 1 }: Props) {
  const specs = useMemo(() => buildPetals(petals), [petals]);
  const auroraRef = useRef<HTMLDivElement>(null);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReducedMotion(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  // Parallax sutil: aurora segue mouse com easing pesado
  useEffect(() => {
    if (reducedMotion) return;
    let raf = 0;
    let targetX = 0, targetY = 0, currentX = 0, currentY = 0;
    const onMove = (e: MouseEvent) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      targetX = ((e.clientX / w) - 0.5) * 2; // -1..1
      targetY = ((e.clientY / h) - 0.5) * 2;
    };
    const tick = () => {
      currentX += (targetX - currentX) * 0.04;
      currentY += (targetY - currentY) * 0.04;
      const el = auroraRef.current;
      if (el) {
        el.style.transform = `translate3d(${currentX * 24}px, ${currentY * 18}px, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener('mousemove', onMove);
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
    };
  }, [reducedMotion]);

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
      style={{ opacity: intensity }}
    >
      <div ref={auroraRef} className="absolute inset-0 will-change-transform">
      {/* aurora estática — gradients radiais com baixa carga */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60vw 60vw at 8% 12%, rgba(79,122,74,0.10), transparent 60%),' +
            'radial-gradient(ellipse 50vw 50vw at 92% 88%, rgba(138,106,71,0.08), transparent 60%)',
        }}
      />

      {/* vinheta */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.45) 100%)',
        }}
      />
      </div>

      {/* pétalas — fixed parent já não rola, então animam só por keyframes */}
      {specs.map((p, i) => (
        <div
          key={i}
          className="absolute animate-drift"
          style={{
            left: p.left,
            top: '-10vh',
            width: p.size,
            height: p.size * 1.5,
            color: 'var(--moss-700)',
            opacity: 0.05,
            animationDelay: p.delay,
            animationDuration: p.duration,
          }}
        >
          <svg
            viewBox="0 0 40 60"
            fill="currentColor"
            style={{ width: '100%', height: '100%', transform: `rotate(${p.rotate})` }}
          >
            <path d={PETAL_PATH} opacity="0.9" />
          </svg>
        </div>
      ))}
    </div>
  );
}

export default AmbientBackground;
